import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  CircularProgress,
  Link,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { BizboxLogo } from "../../../common/components/bizbox-logo";
import { useVerifyTotp } from "../api/hooks";
import { LoadingButton } from "@mui/lab";
import { decrypt } from "../../../common/utils/decryption-service";

// Authenticator (TOTP) login step-up form — feature/totp-mfa.
// The code comes from the user's authenticator app; a single-use backup code also works.
export const MfaTotpForm: React.FC = () => {
  const { handleSubmit } = useForm();
  const theme = useTheme();
  const showLogo = useMediaQuery(theme.breakpoints.down("laptop"));

  const location = useLocation();
  const { mfaToken, email, remember } = location.state || {};

  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [error, setError] = useState("");

  const decryptedEmail = useMemo(() => {
    if (!email || typeof email !== "string") return "";
    try {
      return decrypt(email) || "";
    } catch {
      return "";
    }
  }, [email]);

  const maskEmail = (e: string) => {
    const [local, domain] = e.split("@");
    if (!domain) return e;
    return `${local.slice(0, 2)}**${local.slice(-2)}@${domain}`;
  };

  const { mutate: verifyTotp, isPending } = useVerifyTotp({
    onError: (msg) => setError(typeof msg === "string" ? msg : "Invalid code."),
  });

  const onSubmit = () => {
    setError("");
    const trimmed = code.trim();
    if (!trimmed) {
      setError("Please enter your code.");
      return;
    }
    verifyTotp({ mfaToken, code: trimmed, remember: !!remember });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    // Digits-only for TOTP; allow letters/dashes for backup codes.
    setCode(useBackup ? v.toUpperCase() : v.replace(/\D/g, "").slice(0, 6));
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
        padding: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          height: "inherit",
          maxWidth: "466px",
          width: "100%",
          margin: "auto",
        }}
      >
        {showLogo && (
          <Box sx={{ mt: "20px", display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: "47px", height: "47px" }}>
              <BizboxLogo />
            </Box>
          </Box>
        )}

        <Typography
          fontWeight={500}
          variant="h1"
          sx={{ mb: 6 }}
          align="center"
          color="primary.main"
        >
          Authenticator Code
        </Typography>

        <Typography
          fontSize={16}
          fontWeight={500}
          align="left"
          sx={{ mb: 1 }}
          color="text.secondary"
        >
          Open your authenticator app{decryptedEmail ? ` for ${maskEmail(decryptedEmail)}` : ""} and
          enter the current 6-digit code.
        </Typography>

        <TextField
          data-testid="mfa-totp-code"
          autoFocus
          value={code}
          onChange={handleChange}
          placeholder={useBackup ? "Backup code (e.g. 7QK9-MW3T)" : "123456"}
          variant="outlined"
          fullWidth
          inputProps={{
            inputMode: useBackup ? "text" : "numeric",
            maxLength: useBackup ? 12 : 6,
            style: {
              textAlign: "center",
              fontSize: "1.8rem",
              letterSpacing: useBackup ? "2px" : "10px",
              color: "#123257",
            },
          }}
          sx={{ mt: 2 }}
        />

        {error && (
          <Typography sx={{ mt: 1 }} fontSize={"12px"} color="error" data-testid="mfa-totp-error">
            {error}
          </Typography>
        )}

        <Typography fontSize={"12px"} align="center" sx={{ mt: 3 }} color="#637B9B">
          {useBackup ? "Have your app back? " : "Lost access to your app? "}
          <Link
            fontWeight={500}
            fontSize={"14px"}
            sx={{ ml: 0.5, textDecoration: "none", cursor: "pointer", color: "#FF7F6C" }}
            onClick={() => {
              setUseBackup((v) => !v);
              setCode("");
              setError("");
            }}
          >
            {useBackup ? "Use authenticator code" : "Use a backup code"}
          </Link>
        </Typography>

        <LoadingButton
          data-testid="mfa-totp-submit"
          type="submit"
          loading={isPending}
          loadingIndicator={<CircularProgress size={18} sx={{ color: "white" }} />}
          sx={{
            backgroundColor: "primary.main",
            color: "white",
            mt: 4,
            borderRadius: "12px",
            fontWeight: "bold",
            fontSize: "1.2rem",
          }}
        >
          Verify
        </LoadingButton>
      </Box>
    </Box>
  );
};
